import React from 'react';

import {PartJSON} from 'biojs/Part';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import AttachDropdown from 'ui-library/attach-dropdown/AttachDropdown';
import {AttachDropdownItem} from 'ui-library/attach-dropdown/AttachDropdownConstants';
import {SelectAttachment} from 'ui-library/attach-dropdown/AttachDropdownOptionsMenu';
import {ElementKeyboardEvent} from 'util/Types';

export interface PartSearchDropdownProps {
  sequenceParts: Array<PartJSON>;
  query: string;
  tagSchemaId: string | null;
  selectAttachment: SelectAttachment;
}

export interface PartSearchDropdownHandle {
  queryKeydownHandler: (e: ElementKeyboardEvent) => boolean;
}

// ControlledSearchInput, which this component is used with, uses an imperative pattern of handling
// keydown events from the parent component, so we need to continue supporting that with refs.
const PartSearchDropdown = React.forwardRef<PartSearchDropdownHandle, PartSearchDropdownProps>(
  (props, ref) => {
    const {sequenceParts, query, tagSchemaId, selectAttachment, ...restProps} = props;
    const attachDropdownRef = React.useRef<AttachDropdown>(null);

    function matchSearch(part: PartJSON): boolean {
      return part.name!.toLowerCase().includes(query.toLowerCase());
    }

    function partToResult(part: PartJSON): AttachDropdownItem {
      return {
        name: part.name!,
        item_id: part.sequence!.id,
        item: part.sequence!,
        type: ModelTypeConstants.SEQUENCE,
      };
    }

    function getRecursiveParts(parts: Array<PartJSON>): Array<PartJSON> {
      const recursiveParts = [];
      for (const part of parts) {
        if (part.sequence) {
          recursiveParts.push(part);
          if (part.sequence.parts) {
            recursiveParts.push(...getRecursiveParts(part.sequence.parts));
          }
        }
      }
      return recursiveParts;
    }

    function getValidParts(): Array<PartJSON> {
      return getRecursiveParts(sequenceParts)
        .filter((part) => part.sequence && matchSearch(part))
        .filter(
          // TODO: Handle filtering when the linked ID is a static schema interface ID
          (part) => part.sequence!.tagSchema && part.sequence!.tagSchema.id === tagSchemaId
        );
    }

    React.useImperativeHandle(ref, () => ({
      queryKeydownHandler: (e: ElementKeyboardEvent) => {
        if (!attachDropdownRef.current) {
          return false;
        }
        return attachDropdownRef.current.queryKeydownHandler(e);
      },
    }));

    const results = getValidParts().map((part) => partToResult(part));

    return (
      <AttachDropdown
        ref={attachDropdownRef}
        emptyQueryMessage="No parts available for this field"
        errorMessage={null}
        results={results}
        searching={false}
        width="100%"
        query={query}
        selectAttachment={selectAttachment}
        {...restProps}
      />
    );
  }
);

PartSearchDropdown.displayName = 'PartSearchDropdown';

export default PartSearchDropdown;
